import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from '../components/layout/SiteHeader';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import AuthModal from '../components/auth/AuthModal';
import CheckoutSkeleton from '../components/checkout/CheckoutSkeleton';
import { eventsAPI, ticketsAPI, paymentsAPI, setGuestCheckoutToken } from '../lib/api';
import { resolveMediaUrl } from '../utils/mediaUrl';

const STEPS = [
  { id: 1, label: 'You', full: 'Your details' },
  { id: 2, label: 'Pay', full: 'M-Pesa' },
  { id: 3, label: 'Vibe', full: "You're in" }
];

const FIELD =
  'w-full rounded-lg border border-stone-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-stone-400 ' +
  'outline-none transition-colors ' +
  'hover:border-stone-300 ' +
  'focus:border-orange-400 focus:ring-0 focus:shadow-[0_0_0_3px_rgba(251,146,60,0.18)]';

function isTransientMpesaFailureMessage(message) {
  const msg = String(message || '').toLowerCase();
  return msg.includes('still under processing') || msg.includes('still being processed');
}

function OrderSummary({ event, cartItems, quantity, ticketType, total, billRef, className = '' }) {
  const lineItems = cartItems.length > 0
    ? cartItems
    : [{ name: ticketType || 'General', quantity, price: total / (quantity || 1) }];

  return (
    <div className={`bg-white rounded-2xl border border-stone-200/80 shadow-sm p-5 lg:p-6 lg:sticky lg:top-6 ${className}`}>
      <h2 className="text-base lg:text-lg font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2">
        <span className="w-1 h-4 lg:h-5 rounded-full bg-gradient-to-b from-orange-400 to-orange-600" />
        Your order
      </h2>

      {event && (
        <div className="flex gap-3 lg:gap-4 mb-4 lg:mb-5 pb-4 lg:pb-5 border-b border-stone-100">
          {event.imageUrl && (
            <img
              src={resolveMediaUrl(event.imageUrl)}
              alt={event.title}
              className="w-14 h-14 lg:w-20 lg:h-20 rounded-xl object-cover shrink-0"
            />
          )}
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 leading-snug text-sm lg:text-base">{event.title}</h3>
            <p className="text-xs lg:text-sm text-stone-500 mt-0.5 lg:mt-1">
              {new Date(event.date).toLocaleDateString('en-KE', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </p>
            {event.venue?.name && (
              <p className="text-xs lg:text-sm text-stone-500 truncate">{event.venue.name}</p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2 text-sm">
        {lineItems.map((item, idx) => (
          <div key={item.tierId || idx} className="flex justify-between gap-4">
            <span className="text-stone-600">
              {item.quantity}× {item.name}
            </span>
            <span className="font-medium text-gray-900 shrink-0">
              Ksh {(Number(item.price) * item.quantity).toLocaleString('en-KE')}
            </span>
          </div>
        ))}
        <div className="flex justify-between pt-3 mt-2 border-t border-stone-100">
          <span className="font-semibold text-gray-900">Total</span>
          <span className="font-bold text-lg lg:text-xl text-orange-600">
            Ksh {Number(total).toLocaleString('en-KE')}
          </span>
        </div>
      </div>

      {billRef && (
        <div className="mt-3 lg:mt-4 rounded-xl bg-orange-50 border border-orange-100 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-700 mb-0.5">
            Payment reference
          </p>
          <p className="text-xs font-mono font-medium text-orange-950 break-all">{billRef}</p>
        </div>
      )}
    </div>
  );
}

/** Compact event + total strip for mobile (above the form). */
function MobileOrderStrip({ event, total, ticketLine }) {
  if (!event && !total) return null;

  return (
    <div className="lg:hidden flex items-center gap-3 rounded-xl border border-stone-200/80 bg-white px-3 py-2.5 shadow-sm">
      {event?.imageUrl ? (
        <img
          src={resolveMediaUrl(event.imageUrl)}
          alt=""
          className="w-11 h-11 rounded-lg object-cover shrink-0"
        />
      ) : (
        <div className="w-11 h-11 rounded-lg bg-orange-100 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-gray-900 text-sm leading-snug truncate">
          {event?.title || 'Your tickets'}
        </p>
        {ticketLine && (
          <p className="text-xs text-stone-500 truncate mt-0.5">{ticketLine}</p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="text-[10px] uppercase tracking-wide text-stone-400 font-medium">Total</p>
        <p className="font-bold text-orange-600 text-sm whitespace-nowrap">
          Ksh {Number(total).toLocaleString('en-KE')}
        </p>
      </div>
    </div>
  );
}

const CheckoutPage = () => {
  const { paymentId: routePaymentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [authJustDone, setAuthJustDone] = useState(false);

  const eventId = searchParams.get('event_id');
  const quantity = parseInt(searchParams.get('quantity'), 10) || 1;
  const total = parseFloat(searchParams.get('total')) || 0;
  const ticketType = searchParams.get('ticket_type') || 'regular';
  const cartItems = (() => {
    try {
      const raw = searchParams.get('cart');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  })();

  const [step, setStep] = useState(routePaymentId ? 2 : 1);
  const [event, setEvent] = useState(null);
  const [payment, setPayment] = useState(null);
  const [provider, setProvider] = useState('MPESA_DARAJA');
  const [providerInfo, setProviderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending');

  const [formData, setFormData] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phoneNumber: user?.phone || ''
  });
  const [mpesaPhone, setMpesaPhone] = useState(user?.phone || '');

  // Prefill buyer details when user signs in from the modal (or session restores)
  useEffect(() => {
    if (!user) return;
    setFormData((prev) => {
      const names = String(user.name || '').trim().split(/\s+/).filter(Boolean);
      const firstName = names[0] || prev.firstName;
      const lastName = names.slice(1).join(' ') || prev.lastName;
      return {
        firstName: prev.firstName?.trim() ? prev.firstName : firstName,
        lastName: prev.lastName?.trim() ? prev.lastName : lastName,
        email: prev.email?.trim() ? prev.email : (user.email || ''),
        phoneNumber: prev.phoneNumber?.trim() ? prev.phoneNumber : (user.phone || '')
      };
    });
    setMpesaPhone((prev) => (prev?.trim() ? prev : (user.phone || '')));
  }, [user]);

  const openAuthModal = (mode = 'login') => {
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };

  const handleAuthSuccess = (authedUser) => {
    setAuthJustDone(true);
    const names = String(authedUser?.name || '').trim().split(/\s+/).filter(Boolean);
    setFormData({
      firstName: names[0] || '',
      lastName: names.slice(1).join(' ') || '',
      email: authedUser?.email || '',
      phoneNumber: authedUser?.phone || formData.phoneNumber || ''
    });
    if (authedUser?.phone) setMpesaPhone(authedUser.phone);
    window.setTimeout(() => setAuthJustDone(false), 4000);
  };

  const pollInFlightRef = useRef(false);
  const waitStartedAtRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollInFlightRef.current = false;
  }, []);

  const applyPaymentUpdate = useCallback((p) => {
    setPayment(p);
    if (p.paymentProvider) {
      setProvider(p.paymentProvider);
    }

    if (p.status === 'COMPLETED') {
      setStep(3);
      setPaymentStatus('success');
      setError('');
      stopPolling();
      return;
    }

    if (p.status === 'FAILED') {
      if (isTransientMpesaFailureMessage(p.failureReason || p.statusMessage)) {
        setStep(2);
        setPaymentStatus('processing');
        setError('');
        return;
      }
      setStep(2);
      setPaymentStatus('failed');
      setError(p.statusMessage || p.failureReason || 'Payment was not completed.');
      stopPolling();
      return;
    }

    if (p.status === 'PROCESSING') {
      setStep(2);
      setPaymentStatus('processing');
      setError('');
      return;
    }

    setStep(2);
    setPaymentStatus('pending');
  }, [stopPolling]);

  const pollPaymentOnce = useCallback(async (id, options = {}) => {
    const res = await paymentsAPI.getPaymentStatus(id, options);
    if (!res?.success) return null;
    const p = res.data.payment;
    applyPaymentUpdate(p);
    return p;
  }, [applyPaymentUpdate]);

  const loadProvider = useCallback(async (forEventId) => {
    try {
      const res = await paymentsAPI.getProvider(
        forEventId ? { eventId: forEventId } : {}
      );
      if (res?.data?.provider) setProvider(res.data.provider);
      if (res?.data) setProviderInfo(res.data);
    } catch {
      setProvider('MPESA_DARAJA');
    }
  }, []);

  const loadPayment = useCallback(async (id) => {
    const res = await paymentsAPI.getPayment(id);
    if (!res?.success) throw new Error(res?.message || 'Payment not found');
    const p = res.data.payment;
    setMpesaPhone(p.phoneNumber || user?.phone || '');
    if (p.event) setEvent(p.event);
    if (p.paymentProvider) setProvider(p.paymentProvider);
    applyPaymentUpdate(p);
    return p;
  }, [user?.phone, applyPaymentUpdate]);

  const loadEvent = useCallback(async (id) => {
    const res = await eventsAPI.getEvent(id);
    if (res?.success) setEvent(res.data.event);
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError('');
      try {
        if (routePaymentId) {
          const p = await loadPayment(routePaymentId);
          await loadProvider(p?.eventId || p?.event?.id);
        } else if (eventId) {
          await loadEvent(eventId);
          await loadProvider(eventId);
          setStep(1);
        } else {
          setError('Nothing to grab yet - pick an event first.');
        }
      } catch (err) {
        setError(err.message || 'Could not load this page');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [routePaymentId, eventId, loadPayment, loadEvent, loadProvider]);

  const startPolling = useCallback((id) => {
    stopPolling();
    pollCountRef.current = 0;
    waitStartedAtRef.current = Date.now();

    const tick = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        pollCountRef.current += 1;
        const n = pollCountRef.current;
        // Query often enough to surface wrong-PIN / cancel quickly; avoid stacking slow NCBA calls.
        const useGatewayQuery = provider === 'NCBA_TILL'
          ? n >= 6 && n % 3 === 0 // ~4.5s first query, then ~2.25s
          : n >= 3 && n % 2 === 0; // ~2.25s first Daraja query, then ~1.5s
        await pollPaymentOnce(id, { query: useGatewayQuery });
      } catch {
        // keep polling until timeout
      } finally {
        pollInFlightRef.current = false;
      }
    };

    tick();
    pollRef.current = setInterval(tick, 750);

    setTimeout(() => {
      stopPolling();
      setPaymentStatus((current) => {
        if (current === 'processing') {
          setError('Still waiting on M-Pesa. Tap Try again to send a new prompt.');
          return 'failed';
        }
        return current;
      });
    }, 90000);
  }, [stopPolling, pollPaymentOnce, provider]);

  useEffect(() => {
    if (paymentStatus === 'processing' && payment?.id) {
      startPolling(payment.id);
    }
    return () => stopPolling();
  }, [paymentStatus, payment?.id, startPolling, stopPolling]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateDetails = () => {
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!formData.phoneNumber.trim()) return 'Phone number is required';
    return '';
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    const validationError = validateDetails();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (quantity < 1 || total <= 0) {
      setError('Invalid order. Go back to the event and pick your tickets.');
      return;
    }

    setProcessing(true);
    setError('');
    try {
      const purchaseData = {
        eventId,
        buyerInfo: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phoneNumber: formData.phoneNumber.trim()
        },
        quantity,
        ticketType,
        totalAmount: total,
        ...(cartItems.length > 0 && { items: cartItems })
      };

      const res = await ticketsAPI.purchaseTickets(purchaseData);
      if (!res?.success) throw new Error(res?.message || 'Failed to create order');

      const guestToken = res.data?.guestToken;
      if (guestToken) {
        setGuestCheckoutToken(guestToken);
      } else {
        throw new Error('Checkout session could not be started. Please try again.');
      }

      const newPaymentId = res.data.payment?.id || res.data.paymentId;
      if (!newPaymentId) {
        throw new Error('Payment was not created. Please try again.');
      }

      // Use payment payload from purchase when possible to avoid a race on navigate
      const purchasedPayment = res.data.payment;
      if (purchasedPayment) {
        setMpesaPhone(purchasedPayment.phoneNumber || formData.phoneNumber.trim());
        if (purchasedPayment.event) setEvent(purchasedPayment.event);
        applyPaymentUpdate(purchasedPayment);
      }

      navigate(`/checkout/${newPaymentId}`, { replace: true });
      // Explicit Authorization so we never rely on a stale login JWT
      await paymentsAPI.getPayment(newPaymentId, {
        headers: { Authorization: `Bearer ${guestToken}` },
        timeout: 30000
      }).then((payRes) => {
        if (payRes?.success && payRes.data?.payment) {
          applyPaymentUpdate(payRes.data.payment);
          setMpesaPhone(payRes.data.payment.phoneNumber || formData.phoneNumber.trim());
        }
      }).catch(() => {
        // Payment already applied from purchase response; continue to M-Pesa step
      });

      const completed = purchasedPayment?.status === 'COMPLETED';
      setStep(completed ? 3 : 2);
    } catch (err) {
      const msg = err.message || 'Could not start payment';
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleInitiatePayment = async () => {
    if (!payment?.id) return;
    const phone = mpesaPhone.trim();
    if (!phone) {
      setError('Enter the M-Pesa number that will pay');
      return;
    }

    setProcessing(true);
    setError('');
    try {
      const res = await paymentsAPI.initiatePayment({
        paymentId: payment.id,
        phoneNumber: phone
      });
      if (!res?.success) {
        throw new Error(res?.message || 'Could not send the M-Pesa prompt');
      }
      setPaymentStatus('processing');
      setPayment((prev) => (prev ? { ...prev, status: 'PROCESSING', phoneNumber: phone, failureReason: null } : prev));
      pollPaymentOnce(payment.id, { query: false }).catch(() => {});
    } catch (err) {
      const isTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '');
      const msg = isTimeout
        ? 'M-Pesa is taking longer than usual. Wait a moment, then tap Pay again — do not refresh the page.'
        : (err.message || 'Payment could not start');
      setPaymentStatus('failed');
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryPayment = () => {
    stopPolling();
    setPaymentStatus('pending');
    setError('');
    setPayment((prev) => (prev
      ? { ...prev, status: 'FAILED', failureReason: null, statusMessage: null }
      : prev));
  };

  const displayTotal = payment?.amount ?? total;
  const displayCart = payment?.cartItems?.length ? payment.cartItems : cartItems;
  const displayQuantity = payment?.quantity ?? quantity;
  const billRef = payment?.billRef;
  const paymentFailureMessage = payment?.statusMessage || payment?.failureReason || error;
  const paymentsReady =
    provider === 'FAMILY_BANK'
      ? providerInfo?.familyBankConfigured !== false
      : provider === 'NCBA_TILL'
        ? providerInfo?.ncbaReadyForStk !== false && providerInfo?.ncbaConfigured !== false
        : providerInfo?.callbackReady !== false && providerInfo?.mpesaConfigured !== false;

  const mobileTicketLine = (() => {
    const items = displayCart.length > 0
      ? displayCart
      : [{ name: ticketType || 'General', quantity: displayQuantity }];
    return items
      .map((item) => `${item.quantity}× ${item.name}`)
      .join(', ');
  })();

  if (loading) {
    return (
      <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50/80 to-stone-100">
        <SiteHeader />
        <SiteMain className="flex-1">
          <CheckoutSkeleton />
        </SiteMain>
      </SiteLayout>
    );
  }

  if (error && !event && !payment) {
    return (
      <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50/80 to-stone-100">
        <SiteHeader />
        <SiteMain className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Nothing to grab yet</h2>
            <p className="text-stone-600 mb-6">{error}</p>
            <Link
              to="/events"
              className="inline-block bg-orange-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-orange-600 transition-colors"
            >
              Browse events
            </Link>
          </div>
        </SiteMain>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50/80 to-stone-100">
      <SiteHeader />

      <SiteMain className="py-4 sm:py-8 lg:py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-4 sm:mb-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 border border-orange-100 px-2.5 py-0.5 mb-2 sm:mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
              <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.12em] text-orange-700">
                Secure · M-Pesa
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
              Grab your ticket
            </h1>
            <p className="text-stone-600 mt-1 sm:mt-2 text-sm sm:text-base max-w-xl">
              Pay with M-Pesa, no account needed - drop your details and vibe on.
            </p>
            {!isAuthenticated && step < 3 ? (
              <p className="text-xs sm:text-sm text-stone-500 mt-2 sm:mt-3">
                Already on Niko Vibe?{' '}
                <button
                  type="button"
                  onClick={() => openAuthModal('login')}
                  className="text-orange-600 font-medium hover:text-orange-700 underline underline-offset-2"
                >
                  Log in
                </button>
                {' · '}
                <button
                  type="button"
                  onClick={() => openAuthModal('register')}
                  className="text-orange-600 font-medium hover:text-orange-700 underline underline-offset-2"
                >
                  Create account
                </button>
                {' '}
                so tickets land in your dashboard.
              </p>
            ) : isAuthenticated ? (
              <p className="text-xs sm:text-sm text-stone-600 mt-2 sm:mt-3">
                {authJustDone ? 'You\'re signed in - details filled in. Keep going.' : `Signed in as ${user?.email || user?.name}.`}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
            <div className="lg:col-span-2 space-y-3 sm:space-y-5 order-1">
              <MobileOrderStrip
                event={event}
                total={displayTotal}
                ticketLine={mobileTicketLine}
              />

              <nav aria-label="Checkout steps" className="rounded-xl border border-stone-200/80 bg-white px-2.5 py-2 shadow-sm">
                <ol className="flex items-center gap-0.5 sm:gap-1">
                  {STEPS.map((s, index) => {
                    const done = step > s.id;
                    const active = step === s.id;
                    return (
                      <li key={s.id} className="flex items-center min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${
                              done || active
                                ? 'bg-orange-500 text-white'
                                : 'bg-stone-100 text-stone-400'
                            }`}
                          >
                            {done ? (
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              s.id
                            )}
                          </span>
                          <span
                            className={`text-[11px] sm:text-sm truncate ${
                              active || done ? 'text-gray-900 font-semibold' : 'text-stone-400 font-medium'
                            }`}
                          >
                            {s.full}
                          </span>
                        </div>
                        {index < STEPS.length - 1 && (
                          <span
                            className={`mx-1.5 h-px flex-1 min-w-[6px] ${
                              step > s.id ? 'bg-orange-300' : 'bg-stone-200'
                            }`}
                            aria-hidden
                          />
                        )}
                      </li>
                    );
                  })}
                </ol>
              </nav>

              {error && paymentStatus !== 'failed' && (
                <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl px-3 py-2.5 text-sm">
                  {error}
                </div>
              )}

              {!paymentsReady && step === 2 && (
                <div className="bg-stone-50 border border-stone-200 text-stone-700 rounded-xl px-3 py-2.5 text-sm">
                  Payments are taking a quick pause. Try again in a moment, or hit us up if it keeps happening.
                </div>
              )}

              {step === 1 && (
                <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm p-4 sm:p-6 lg:p-8">
                  <div className="mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Who&apos;s grabbing this?</h2>
                    <p className="text-xs sm:text-sm text-stone-500 mt-0.5">
                      We&apos;ll send your tickets here - make it real.
                    </p>
                  </div>
                  <form onSubmit={handleCreatePayment} className="space-y-3 sm:space-y-4">
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-stone-700 mb-1">First name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          autoComplete="given-name"
                          className={FIELD}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-stone-700 mb-1">Last name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          autoComplete="family-name"
                          className={FIELD}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-stone-700 mb-1">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        autoComplete="email"
                        className={FIELD}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-stone-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="07XX XXX XXX"
                        autoComplete="tel"
                        className={FIELD}
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={processing}
                      className="w-full mt-1 bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 shadow-md shadow-orange-500/20 transition-colors"
                    >
                      {processing ? 'Locking your spot…' : 'Continue to M-Pesa'}
                    </button>
                  </form>
                </div>
              )}

              {step === 2 && (
                <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm p-4 sm:p-6 lg:p-8">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Pay with M-Pesa</h2>
                    <span className="inline-flex items-center rounded-full bg-orange-50 border border-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                      Lipa na M-Pesa
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-stone-500 mb-4">
                    We&apos;ll ping your phone. Unlock with your M-Pesa PIN and you&apos;re in.
                  </p>

                  {paymentStatus === 'processing' ? (
                    <div className="text-center py-6">
                      <div className="relative mx-auto w-14 h-14 mb-4">
                        <div className="absolute inset-0 rounded-full bg-orange-100 animate-ping opacity-40" />
                        <div className="relative animate-spin rounded-full h-full w-full border-[3px] border-orange-100 border-t-orange-500" />
                      </div>
                      <p className="font-semibold text-gray-900">Check your phone</p>
                      <p className="text-xs sm:text-sm text-stone-500 mt-2 max-w-sm mx-auto">
                        Approve the M-Pesa prompt with your PIN. If you cancel or enter the wrong PIN, you can try again below.
                      </p>
                      {billRef && (
                        <p className="mt-3 text-xs text-orange-800 font-mono bg-orange-50 inline-block px-3 py-1.5 rounded-lg border border-orange-100">
                          Ref: {billRef}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleRetryPayment}
                        className="mt-5 w-full border border-stone-200 bg-white text-stone-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-stone-50 transition-colors"
                      >
                        Cancel &amp; try again
                      </button>
                    </div>
                  ) : paymentStatus === 'failed' ? (
                    <div className="text-center py-4">
                      <div className="w-14 h-14 bg-red-50 border border-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <p className="font-semibold text-gray-900 mb-2">That one didn&apos;t go through</p>
                      <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-4">
                        {paymentFailureMessage || 'Payment wasn\'t completed. Hit retry when you\'re ready.'}
                      </p>
                      <button
                        type="button"
                        onClick={handleRetryPayment}
                        className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
                      >
                        Try again
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4">
                        <label className="block text-xs sm:text-sm font-medium text-stone-700 mb-1">
                          M-Pesa number
                        </label>
                        <input
                          type="tel"
                          value={mpesaPhone}
                          onChange={(e) => setMpesaPhone(e.target.value)}
                          placeholder="07XX XXX XXX"
                          autoComplete="tel"
                          className={FIELD}
                        />
                        <p className="mt-1.5 text-xs text-stone-400">
                          Use the number that will receive the prompt.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleInitiatePayment}
                        disabled={processing || !paymentsReady}
                        className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 shadow-md shadow-orange-500/20 transition-colors"
                      >
                        {processing
                          ? 'Sending prompt…'
                          : `Pay Ksh ${Number(displayTotal).toLocaleString('en-KE')}`}
                      </button>
                    </>
                  )}
                </div>
              )}

              {step === 3 && payment && (
                <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm p-4 sm:p-6 lg:p-8 text-center">
                  <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-orange-500/25">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">You&apos;re in</h2>
                  <p className="text-stone-600 text-sm mb-2">Tickets unlocked - go vibe.</p>
                  <p className="text-xs sm:text-sm text-stone-500 mb-4">
                    We emailed your ticket links to{' '}
                    <span className="font-medium text-gray-700">{formData.email || payment.buyerEmail}</span>.
                  </p>
                  {payment.mpesaReceiptNumber && (
                    <p className="text-xs text-stone-500 font-mono mb-4">
                      Receipt: {payment.mpesaReceiptNumber}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
                    {payment.tickets?.length > 0 ? (
                      payment.tickets.map((t) => (
                        <Link
                          key={t.id}
                          to={`/tickets/${t.id}`}
                          className="inline-block bg-orange-500 text-white px-5 py-3 rounded-xl font-medium hover:bg-orange-600 transition-colors shadow-md shadow-orange-500/20"
                        >
                          View ticket{payment.tickets.length > 1 ? ` #${t.ticketNumber || ''}` : ''}
                        </Link>
                      ))
                    ) : (
                      <Link
                        to="/dashboard"
                        className="inline-block bg-orange-500 text-white px-5 py-3 rounded-xl font-medium hover:bg-orange-600 transition-colors"
                      >
                        Go to dashboard
                      </Link>
                    )}
                  </div>
                </div>
              )}

              <details className="lg:hidden group rounded-xl border border-stone-200/80 bg-white open:shadow-sm">
                <summary className="cursor-pointer list-none px-3 py-2.5 text-sm font-medium text-stone-700 flex items-center justify-between">
                  <span>Full order details</span>
                  <span className="text-stone-400 text-xs group-open:hidden">Show</span>
                  <span className="text-stone-400 text-xs hidden group-open:inline">Hide</span>
                </summary>
                <div className="px-1 pb-1">
                  <OrderSummary
                    className="border-0 shadow-none rounded-xl"
                    event={event}
                    cartItems={displayCart}
                    quantity={displayQuantity}
                    ticketType={ticketType}
                    total={displayTotal}
                    billRef={billRef}
                  />
                </div>
              </details>
            </div>

            <div className="hidden lg:block lg:col-span-1 order-2">
              <OrderSummary
                event={event}
                cartItems={displayCart}
                quantity={displayQuantity}
                ticketType={ticketType}
                total={displayTotal}
                billRef={billRef}
              />
            </div>
          </div>
        </div>
      </SiteMain>

      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
        initialMode={authModalMode}
        prefillEmail={formData.email}
        contextHint="Stay on this page - your tickets stay right here."
      />
    </SiteLayout>
  );
};

export default CheckoutPage;
